import React,{createContext,useContext,useEffect,useState} from 'react'
import {supabase,TABLES} from '../config/supabase'
import {useAuth} from './AuthContext'
import toast from 'react-hot-toast'

const FamilyContext=createContext({})

export const useFamily=()=> {
  const context=useContext(FamilyContext)
  if (!context) {
    throw new Error('useFamily must be used within a FamilyProvider')
  }
  return context
}

export const FamilyProvider=({children})=> {
  const {user,family}=useAuth()
  const [tasks,setTasks]=useState([])
  const [rewards,setRewards]=useState([])
  const [completions,setCompletions]=useState([])
  const [redemptions,setRedemptions]=useState([])
  const [loading,setLoading]=useState(false)

  useEffect(()=> {
    if (family) {
      loadFamilyData()
      subscribeToChanges()
    }
  },[family])

  const loadFamilyData=async ()=> {
    if (!family) return
    
    setLoading(true)
    try {
      // Load tasks with error handling
      const {data: tasksData,error: tasksError}=await supabase
        .from(TABLES.TASKS)
        .select('*')
        .eq('family_id',family.id)
        .or('is_active.is.null,is_active.eq.true') // Handle both null and true values
        .order('created_at',{ascending: false})

      if (tasksError) {
        console.error('Error loading tasks:',tasksError)
      } else {
        setTasks(tasksData || [])
      }

      // Load rewards with error handling - handle missing is_active column gracefully
      const {data: rewardsData,error: rewardsError}=await supabase
        .from(TABLES.REWARDS)
        .select('*')
        .eq('family_id',family.id)
        .or('is_active.is.null,is_active.eq.true') // Handle both null and true values
        .order('points_required',{ascending: true})

      if (rewardsError) {
        console.error('Error loading rewards:',rewardsError)
      } else {
        setRewards(rewardsData || [])
      }

      // Load completions with error handling
      const {data: completionsData,error: completionsError}=await supabase
        .from(TABLES.TASK_COMPLETIONS)
        .select(`
          *,
          tasks:${TABLES.TASKS} (*),
          children:${TABLES.CHILDREN} (*)
        `)
        .eq('family_id',family.id)
        .order('completed_at',{ascending: false})

      if (completionsError) {
        console.error('Error loading completions:',completionsError)
      } else {
        setCompletions(completionsData || [])
      }

      // Load reward redemptions
      const {data: redemptionsData,error: redemptionsError}=await supabase
        .from(TABLES.REWARD_REDEMPTIONS)
        .select(`
          *,
          rewards:${TABLES.REWARDS} (*),
          children:${TABLES.CHILDREN} (*)
        `)
        .eq('family_id',family.id)
        .order('redeemed_at',{ascending: false})

      if (redemptionsError) {
        console.error('Error loading redemptions:',redemptionsError)
      } else {
        setRedemptions(redemptionsData || [])
      }

    } catch (error) {
      console.error('Error loading family data:',error)
      toast.error('Error loading family data')
    } finally {
      setLoading(false)
    }
  }

  const subscribeToChanges=()=> {
    if (!family) return

    // Subscribe to task changes
    const tasksSubscription=supabase
      .channel('tasks')
      .on(
        'postgres_changes',
        {event: '*',schema: 'public',table: TABLES.TASKS,filter: `family_id=eq.${family.id}`},
        (payload)=> {
          if (payload.eventType==='INSERT') {
            setTasks(prev=> [payload.new,...prev])
          } else if (payload.eventType==='UPDATE') {
            setTasks(prev=> 
              prev.map(task=> 
                task.id===payload.new.id ? payload.new : task
              )
            )
          } else if (payload.eventType==='DELETE') {
            setTasks(prev=> prev.filter(task=> task.id !==payload.old.id))
          }
        }
      )
      .subscribe()

    // Subscribe to rewards changes
    const rewardsSubscription=supabase
      .channel('rewards')
      .on(
        'postgres_changes',
        {event: '*',schema: 'public',table: TABLES.REWARDS,filter: `family_id=eq.${family.id}`},
        (payload)=> {
          if (payload.eventType==='INSERT') {
            setRewards(prev=> 
              [...prev,payload.new].sort((a,b)=> a.points_required - b.points_required)
            )
          } else if (payload.eventType==='UPDATE') {
            setRewards(prev=> 
              prev.map(reward=> 
                reward.id===payload.new.id ? payload.new : reward
              )
            )
          } else if (payload.eventType==='DELETE') {
            setRewards(prev=> prev.filter(reward=> reward.id !==payload.old.id))
          }
        }
      )
      .subscribe()

    // Subscribe to completions
    const completionsSubscription=supabase
      .channel('completions')
      .on(
        'postgres_changes',
        {event: '*',schema: 'public',table: TABLES.TASK_COMPLETIONS,filter: `family_id=eq.${family.id}`},
        (payload)=> {
          if (payload.eventType==='INSERT') {
            loadFamilyData() // Reload to get joined data
          }
        }
      )
      .subscribe()

    // Subscribe to reward redemptions
    const redemptionsSubscription=supabase
      .channel('redemptions')
      .on(
        'postgres_changes',
        {event: '*',schema: 'public',table: TABLES.REWARD_REDEMPTIONS,filter: `family_id=eq.${family.id}`},
        (payload)=> {
          console.log('Redemption change detected:', payload)
          if (payload.eventType==='INSERT') {
            loadFamilyData() // Reload to get joined data and update points
          }
        }
      )
      .subscribe()

    return ()=> {
      tasksSubscription.unsubscribe()
      rewardsSubscription.unsubscribe()
      completionsSubscription.unsubscribe()
      redemptionsSubscription.unsubscribe()
    }
  }

  const createTask=async (taskData)=> {
    try {
      console.log('FamilyContext createTask called with:',taskData)
      
      if (!family) {
        console.error('No family found in context')
        return {data: null,error: {message: 'No family found'}}
      }

      if (!user) {
        console.error('No user found in context')
        return {data: null,error: {message: 'No user found'}}
      }

      // Prepare the task data - include all new fields
      const taskToInsert={
        title: taskData.title,
        description: taskData.description || null,
        points: taskData.points || 10,
        category: taskData.category || 'other',
        assigned_to: taskData.assigned_to || null,
        recurring_pattern: taskData.recurring_pattern || 'once',
        recurring_days: taskData.recurring_days || null,
        start_date: taskData.start_date || new Date().toISOString().split('T')[0],
        end_date: taskData.end_date || null,
        due_date: taskData.due_date || null,
        estimated_time_minutes: taskData.estimated_time_minutes || null,
        family_id: family.id,
        is_active: true // Explicitly set is_active
      }

      console.log('Inserting task:',taskToInsert)

      const {data,error}=await supabase
        .from(TABLES.TASKS)
        .insert([taskToInsert])
        .select()
        .single()

      if (error) {
        console.error('Supabase error creating task:',error)
        return {data: null,error}
      }

      console.log('Task created successfully:',data)
      return {data,error: null}

    } catch (error) {
      console.error('Unexpected error creating task:',error)
      return {data: null,error: {message: error.message || 'Unexpected error occurred'}}
    }
  }

  const updateTask=async (taskId,taskData)=> {
    try {
      console.log('FamilyContext updateTask called with:',taskId,taskData)
      
      if (!family) {
        console.error('No family found in context')
        return {data: null,error: {message: 'No family found'}}
      }

      if (!user) {
        console.error('No user found in context')
        return {data: null,error: {message: 'No user found'}}
      }

      // Prepare the updated task data
      const taskToUpdate={
        title: taskData.title,
        description: taskData.description || null,
        points: taskData.points || 10,
        category: taskData.category || 'other',
        assigned_to: taskData.assigned_to || null,
        recurring_pattern: taskData.recurring_pattern || 'once',
        recurring_days: taskData.recurring_days || null,
        start_date: taskData.start_date,
        end_date: taskData.end_date || null,
        due_date: taskData.due_date || null,
        estimated_time_minutes: taskData.estimated_time_minutes || null,
        updated_at: new Date().toISOString()
      }

      console.log('Updating task:',taskToUpdate)

      const {data,error}=await supabase
        .from(TABLES.TASKS)
        .update(taskToUpdate)
        .eq('id',taskId)
        .eq('family_id',family.id) // Ensure user can only update their family's tasks
        .select()
        .single()

      if (error) {
        console.error('Supabase error updating task:',error)
        return {data: null,error}
      }

      console.log('Task updated successfully:',data)
      return {data,error: null}

    } catch (error) {
      console.error('Unexpected error updating task:',error)
      return {data: null,error: {message: error.message || 'Unexpected error occurred'}}
    }
  }

  const deleteTask=async (taskId)=> {
    try {
      console.log('FamilyContext deleteTask called with:',taskId)
      
      if (!family) {
        console.error('No family found in context')
        return {data: null,error: {message: 'No family found'}}
      }

      if (!user) {
        console.error('No user found in context')
        return {data: null,error: {message: 'No user found'}}
      }

      // Soft delete by setting is_active to false
      const {data,error}=await supabase
        .from(TABLES.TASKS)
        .update({
          is_active: false,
          updated_at: new Date().toISOString()
        })
        .eq('id',taskId)
        .eq('family_id',family.id) // Ensure user can only delete their family's tasks
        .select()
        .single()

      if (error) {
        console.error('Supabase error deleting task:',error)
        return {data: null,error}
      }

      console.log('Task deleted successfully:',data)
      return {data,error: null}

    } catch (error) {
      console.error('Unexpected error deleting task:',error)
      return {data: null,error: {message: error.message || 'Unexpected error occurred'}}
    }
  }

  const completeTask=async (taskId,childId,enhancedData = null)=> {
    try {
      const task=tasks.find(t=> t.id===taskId)
      if (!task) {
        return {data: null,error: {message: 'Task not found'}}
      }

      if (!family) {
        return {data: null,error: {message: 'No family found'}}
      }

      // Calculate enhanced points if gamification is enabled
      let finalPoints = task.points
      let bonusPoints = 0
      let multiplier = 1.0
      let bonusReasons = []

      if (enhancedData && family.gamification_settings) {
        try {
          // Call the database function to calculate enhanced points
          const { data: pointsData, error: pointsError } = await supabase
            .rpc('calculate_enhanced_points', {
              p_family_id: family.id,
              p_task_id: taskId,
              p_child_id: childId,
              p_completion_time_seconds: enhancedData.completionTimeSeconds,
              p_quality_rating: enhancedData.qualityRating,
              p_is_helping_sibling: enhancedData.isHelpingSibling
            })

          if (!pointsError && pointsData) {
            finalPoints = pointsData.final_points
            bonusPoints = pointsData.bonus_points
            multiplier = pointsData.multiplier
            bonusReasons = pointsData.bonus_reasons || []
          }
        } catch (pointsCalcError) {
          console.warn('Error calculating enhanced points, using base points:', pointsCalcError)
        }
      }

      // Insert completion record
      const completionData = {
        task_id: taskId,
        child_id: childId,
        family_id: family.id,
        points_earned: finalPoints,
        bonus_points: bonusPoints,
        multiplier_applied: multiplier,
        bonus_reasons: bonusReasons
      }

      // Add enhanced data if provided
      if (enhancedData) {
        if (enhancedData.completionTimeSeconds) {
          completionData.completion_time_seconds = enhancedData.completionTimeSeconds
        }
        if (enhancedData.qualityRating) {
          completionData.quality_rating = enhancedData.qualityRating
        }
      }

      const {data,error}=await supabase
        .from(TABLES.TASK_COMPLETIONS)
        .insert([completionData])
        .select()
        .single()

      if (error) {
        console.error('Error completing task:',error)
        return {data: null,error}
      }

      // Show success message with bonus information
      let message = `Great job! +${finalPoints} points earned! 🎉`
      if (bonusReasons.length > 0) {
        message += ` (${bonusReasons.join(', ')})`
      }
      toast.success(message)

      return {data,error: null}

    } catch (error) {
      console.error('Unexpected error completing task:',error)
      return {data: null,error: {message: error.message || 'Unexpected error occurred'}}
    }
  }

  const redeemReward=async (rewardId,childId)=> {
    try {
      if (!family) {
        return {data: null,error: {message: 'No family found'}}
      }

      const reward = rewards.find(r => r.id === rewardId)
      if (!reward) {
        return {data: null,error: {message: 'Reward not found'}}
      }

      const childPoints = getChildPoints(childId)
      if (childPoints < reward.points_required) {
        return {data: null,error: {message: 'Not enough points'}}
      }

      // Insert redemption record
      const redemptionData = {
        reward_id: rewardId,
        child_id: childId,
        family_id: family.id,
        points_spent: reward.points_required
      }

      const {data,error}=await supabase
        .from(TABLES.REWARD_REDEMPTIONS)
        .insert([redemptionData])
        .select()
        .single()

      if (error) {
        console.error('Error redeeming reward:',error)
        return {data: null,error}
      }

      // Force reload of family data to ensure all views update
      console.log('Reward redeemed successfully, reloading family data...')
      await loadFamilyData()

      toast.success(`Reward "${reward.title}" redeemed successfully! 🎉`)

      return {data,error: null}

    } catch (error) {
      console.error('Unexpected error redeeming reward:',error)
      return {data: null,error: {message: error.message || 'Unexpected error occurred'}}
    }
  }

  const createReward=async (rewardData)=> {
    try {
      console.log('FamilyContext createReward called with:',rewardData)
      
      if (!family) {
        console.error('No family found in context')
        return {data: null,error: {message: 'No family found'}}
      }

      if (!user) {
        console.error('No user found in context')
        return {data: null,error: {message: 'No user found'}}
      }

      const rewardToInsert={
        title: rewardData.title,
        description: rewardData.description || null,
        points_required: rewardData.points_required,
        emoji: rewardData.emoji || '🎁',
        family_id: family.id,
        is_active: true, // Explicitly set is_active
        created_at: new Date().toISOString()
      }

      console.log('Inserting reward:',rewardToInsert)

      const {data,error}=await supabase
        .from(TABLES.REWARDS)
        .insert([rewardToInsert])
        .select()
        .single()

      if (error) {
        console.error('Supabase error creating reward:',error)
        toast.error(`Failed to create reward: ${error.message || 'Unknown error'}`)
        return {data: null,error}
      }

      console.log('Reward created successfully:',data)
      
      // Update local state immediately
      setRewards(prev=> 
        [...prev,data].sort((a,b)=> a.points_required - b.points_required)
      )
      
      toast.success(`Reward "${rewardData.title}" created successfully! 🎁`)
      return {data,error: null}

    } catch (error) {
      console.error('Unexpected error creating reward:',error)
      toast.error('An unexpected error occurred while creating the reward')
      return {data: null,error: {message: error.message || 'Unexpected error occurred'}}
    }
  }

  const updateReward=async (rewardId,rewardData)=> {
    try {
      console.log('FamilyContext updateReward called with:',rewardId,rewardData)
      
      if (!family) {
        console.error('No family found in context')
        return {data: null,error: {message: 'No family found'}}
      }

      if (!user) {
        console.error('No user found in context')
        return {data: null,error: {message: 'No user found'}}
      }

      const rewardToUpdate={
        title: rewardData.title,
        description: rewardData.description || null,
        points_required: rewardData.points_required,
        emoji: rewardData.emoji || '🎁',
        updated_at: new Date().toISOString()
      }

      console.log('Updating reward:',rewardToUpdate)

      const {data,error}=await supabase
        .from(TABLES.REWARDS)
        .update(rewardToUpdate)
        .eq('id',rewardId)
        .eq('family_id',family.id) // Ensure user can only update their family's rewards
        .select()
        .single()

      if (error) {
        console.error('Supabase error updating reward:',error)
        toast.error(`Failed to update reward: ${error.message || 'Unknown error'}`)
        return {data: null,error}
      }

      console.log('Reward updated successfully:',data)
      
      // Update local state immediately
      setRewards(prev=> 
        prev.map(reward=> 
          reward.id===rewardId ? data : reward
        ).sort((a,b)=> a.points_required - b.points_required)
      )
      
      toast.success(`Reward "${rewardData.title}" updated successfully! 🎁`)
      return {data,error: null}

    } catch (error) {
      console.error('Unexpected error updating reward:',error)
      toast.error('An unexpected error occurred while updating the reward')
      return {data: null,error: {message: error.message || 'Unexpected error occurred'}}
    }
  }

  const deleteReward=async (rewardId)=> {
    try {
      console.log('FamilyContext deleteReward called with:',rewardId)
      
      if (!family) {
        console.error('No family found in context')
        return {data: null,error: {message: 'No family found'}}
      }

      if (!user) {
        console.error('No user found in context')
        return {data: null,error: {message: 'No user found'}}
      }

      // Soft delete by setting is_active to false
      const {data,error}=await supabase
        .from(TABLES.REWARDS)
        .update({
          is_active: false,
          updated_at: new Date().toISOString()
        })
        .eq('id',rewardId)
        .eq('family_id',family.id) // Ensure user can only delete their family's rewards
        .select()
        .single()

      if (error) {
        console.error('Supabase error deleting reward:',error)
        toast.error(`Failed to delete reward: ${error.message || 'Unknown error'}`)
        return {data: null,error}
      }

      console.log('Reward deleted successfully:',data)
      
      // Update local state immediately
      setRewards(prev=> prev.filter(reward=> reward.id !==rewardId))
      
      toast.success('Reward deleted successfully!')
      return {data,error: null}

    } catch (error) {
      console.error('Unexpected error deleting reward:',error)
      toast.error('An unexpected error occurred while deleting the reward')
      return {data: null,error: {message: error.message || 'Unexpected error occurred'}}
    }
  }

  const getChildPoints=(childId)=> {
    const pointsEarned = completions
      .filter(completion=> completion.child_id===childId)
      .reduce((total,completion)=> total + (completion.points_earned || 0),0)
    
    const pointsSpent = redemptions
      .filter(redemption=> redemption.child_id===childId)
      .reduce((total,redemption)=> total + (redemption.points_spent || 0),0)
    
    const totalPoints = pointsEarned - pointsSpent
    console.log(`Child ${childId} points: ${pointsEarned} earned - ${pointsSpent} spent = ${totalPoints}`)
    return totalPoints
  }

  const getChildLevel=(points)=> {
    if (points >=500) return {level: 5,title: 'Superhero',next: 1000}
    if (points >=200) return {level: 4,title: 'Champion',next: 500}
    if (points >=100) return {level: 3,title: 'Star',next: 200}
    if (points >=50) return {level: 2,title: 'Helper',next: 100}
    return {level: 1,title: 'Beginner',next: 50}
  }

  const getTasksForChild=(childId)=> {
    return tasks.filter(task=> 
      !task.assigned_to || task.assigned_to===childId
    )
  }

  const getUpcomingTasks=(childId=null)=> {
    const today=new Date()
    const todayStr=today.toISOString().split('T')[0]
    
    return tasks.filter(task=> {
      // Check if task is assigned to child or available to all
      const isAssigned=!childId || !task.assigned_to || task.assigned_to===childId
      
      // Check if task is active and within date range
      const isActive=task.is_active !==false
      const hasStarted=!task.start_date || task.start_date <=todayStr
      const hasNotEnded=!task.end_date || task.end_date >=todayStr
      
      return isAssigned && isActive && hasStarted && hasNotEnded
    })
  }

  const value={
    tasks,
    rewards,
    completions,
    redemptions,
    loading,
    createTask,
    updateTask,
    deleteTask,
    completeTask,
    redeemReward,
    createReward,
    updateReward,
    deleteReward,
    getChildPoints,
    getChildLevel,
    getTasksForChild,
    getUpcomingTasks,
    loadFamilyData
  }

  return (
    <FamilyContext.Provider value={value}>
      {children}
    </FamilyContext.Provider>
  )
}